import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, formatEuro, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AppHeader from "@/components/AppHeader";
import SeatPlan from "@/components/SeatPlan";
import CashConfirmDialog from "@/components/CashConfirmDialog";
import StartCashDialog from "@/components/StartCashDialog";
import SeatPickerModal from "@/components/SeatPickerModal";
import { HelpButton, HelpDialog, useFirstVisitTour } from "@/components/KassaHelp";
import {
  Plus, Minus, Check, Search, FileText, Banknote, Receipt, FileSpreadsheet,
  X, ChevronLeft, Mail, Loader2, Ticket, Trash2, Baby, User as UserIcon,
  BadgeCheck, AlertTriangle, Wallet, Pencil, Users, DoorOpen, Undo2
} from "lucide-react";

const PRICE_KEYS = {
  kinder_res: 7, erwachsene_res: 15, sav_res: 12,
  kinder_no_res: 7, erwachsene_no_res: 15, sav_no_res: 12,
};

function sortSeatKey(a, b) {
  const [ar, as_] = a.split("-").map(Number);
  const [br, bs_] = b.split("-").map(Number);
  return ar - br || as_ - bs_;
}

function friendlyError(detail) {
  if (!detail) return "Etwas ist schiefgelaufen. Bitte nochmal versuchen.";
  const text = (formatApiErrorDetail(detail) || String(detail)).toLowerCase();
  if (text.includes("nicht mehr frei") || text.includes("bereits bezahlt")) return "Dieser Platz ist schon verkauft. Bitte einen anderen wählen.";
  if (text.includes("nicht teil")) return "Dieser Sitz gehört nicht zur gewählten Reservierung.";
  if (text.includes("storniert")) return "Dieser Platz wurde storniert. Bitte einen anderen wählen.";
  if (text.includes("mehr gutscheine")) return "Du hast mehr Gutscheine eingegeben als Karten.";
  if (text.includes("kleiner als der gesamtpreis")) return "Der gegebene Betrag ist kleiner als zu zahlen.";
  if (text.includes("keine karten")) return "Bitte erst mindestens eine Karte hinzufügen.";
  return formatApiErrorDetail(detail) || String(detail);
}

// ============================================================
// Step 1 — Mode selection screen
// ============================================================
function ModeSelectScreen({ onPickReservation, onPickWalkIn, onPickRefund }) {
  return (
    <div className="flex-1 flex items-center justify-center p-8 bg-zinc-50">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl w-full">
        <button
          onClick={onPickReservation}
          data-testid="step-mode-reservation"
          className="bg-white border-2 border-zinc-200 hover:border-blue-500 hover:bg-blue-50 rounded-3xl p-10 shadow-sm hover:shadow-lg transition group text-left">
          <div className="h-20 w-20 rounded-2xl bg-blue-100 group-hover:bg-blue-200 flex items-center justify-center mb-6">
            <Users className="h-12 w-12 text-blue-700" />
          </div>
          <h2 className="text-3xl font-black tracking-tight mb-2">Reservierung</h2>
          <p className="text-base text-zinc-600">Gäste mit vorgedruckten Karten kassieren</p>
        </button>

        <button
          onClick={onPickWalkIn}
          data-testid="step-mode-walkin"
          className="bg-white border-2 border-zinc-200 hover:border-emerald-500 hover:bg-emerald-50 rounded-3xl p-10 shadow-sm hover:shadow-lg transition group text-left">
          <div className="h-20 w-20 rounded-2xl bg-emerald-100 group-hover:bg-emerald-200 flex items-center justify-center mb-6">
            <DoorOpen className="h-12 w-12 text-emerald-700" />
          </div>
          <h2 className="text-3xl font-black tracking-tight mb-2">Abendkasse</h2>
          <p className="text-base text-zinc-600">Gäste ohne Reservierung — Sitz frei wählen</p>
        </button>

        <button
          onClick={onPickRefund}
          data-testid="step-mode-refund"
          className="bg-white border-2 border-zinc-200 hover:border-red-500 hover:bg-red-50 rounded-3xl p-10 shadow-sm hover:shadow-lg transition group text-left">
          <div className="h-20 w-20 rounded-2xl bg-red-100 group-hover:bg-red-200 flex items-center justify-center mb-6">
            <Undo2 className="h-12 w-12 text-red-700" />
          </div>
          <h2 className="text-3xl font-black tracking-tight mb-2">Retoure</h2>
          <p className="text-base text-zinc-600">Bezahlte Karte zurückgeben — Sitz wird wieder frei</p>
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Step 2 — Reservation picker
// ============================================================
function ReservationPickerScreen({ reservations, onPick, onBack }) {
  const [search, setSearch] = useState("");
  const filtered = reservations.filter((r) => {
    if (r.status === "cancelled") return false;
    const open = (r.open_seats || []).length;
    if (open === 0 && r.status === "paid") return false;
    return r.name.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="flex-1 flex flex-col bg-zinc-50 min-h-0">
      <div className="px-6 py-4 bg-white border-b-2 border-zinc-100 flex items-center gap-4 shrink-0">
        <button onClick={onBack} data-testid="step-back-mode"
          className="h-14 px-5 rounded-xl border-2 border-red-300 bg-red-50 hover:bg-red-100 text-red-800 font-black text-lg flex items-center gap-2">
          <ChevronLeft className="h-6 w-6" /> Zurück zur Modus-Wahl
        </button>
        <div className="flex-1">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-zinc-500">Schritt 2</p>
          <h2 className="text-2xl font-black">Welche Reservierung?</h2>
        </div>
      </div>

      <div className="px-6 py-4 bg-white border-b border-zinc-100 shrink-0">
        <div className="flex items-center gap-2">
          <Search className="h-6 w-6 text-zinc-400 ml-2" />
          <input data-testid="kassa-search-input" autoFocus
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Name eingeben…"
            className="flex-1 h-16 px-4 text-2xl border-none focus:outline-none bg-transparent font-bold" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 min-h-0">
        {filtered.length === 0 ? (
          <p className="text-center text-zinc-500 text-lg py-10">Keine offenen Reservierungen.</p>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pb-10">
            {filtered.map((r) => {
              const open = (r.open_seats || []).length;
              const paid = (r.paid_seats || []).length;
              return (
                <li key={r.id}>
                  <button
                    data-testid={`kassa-res-${r.id}`}
                    onClick={() => onPick(r)}
                    className="w-full bg-white border-2 border-zinc-200 hover:border-blue-500 hover:bg-blue-50 rounded-xl p-5 text-left transition">
                    <p className="font-black text-xl mb-2 truncate">{r.name}</p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-600">{r.total_seats} Karten</span>
                      <span className="font-mono font-bold text-blue-700">{open} offen{paid > 0 ? ` · ${paid}✓` : ""}</span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Step 3 — Kassieren screen (reservation OR walk-in)
// ============================================================
function CashierScreen({
  mode, show, showId, selectedRes, batchSeats, walkInSeats,
  reservations, seatRefresh, setSeatRefresh, config,
  onBack, onBackToSeats, onSuccess, navigate, showErr, err, setErr,
}) {
  // For reservation mode: batchSeats is fixed; conversions tracks Kind/SAV changes.
  // Each entry in conversions is { seatKey, cat } where cat is "kind" | "sav".
  const [conversions, setConversions] = useState([]); // history (LIFO for undo)
  const [voucher, setVoucher] = useState(0);
  const [given, setGiven] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [showCashConfirm, setShowCashConfirm] = useState(false);
  const [stornoOpen, setStornoOpen] = useState(false);

  // For walk-in mode: per-seat cart (similar to before)
  const [walkCart, setWalkCart] = useState({}); // { seatKey: "kinder_no_res" | ... }
  const [walkOrder, setWalkOrder] = useState([]);
  const [walkDefault, setWalkDefault] = useState("erwachsene_no_res");

  const isReservation = mode === "reservation";

  // ============= Helpers (reservation mode) =============
  const batchSize = batchSeats.length;
  const kindCount = conversions.filter((c) => c.cat === "kind").length;
  const savCount = conversions.filter((c) => c.cat === "sav").length;
  const erwCount = batchSize - kindCount - savCount;

  // ============= Helpers (walk-in mode) =============
  const walkSeats = useMemo(() => Object.keys(walkCart), [walkCart]);
  const walkErwCount = useMemo(() => walkSeats.filter((k) => walkCart[k] === "erwachsene_no_res").length, [walkSeats, walkCart]);
  const walkKindCount = useMemo(() => walkSeats.filter((k) => walkCart[k] === "kinder_no_res").length, [walkSeats, walkCart]);
  const walkSavCount = useMemo(() => walkSeats.filter((k) => walkCart[k] === "sav_no_res").length, [walkSeats, walkCart]);

  // ============= Aggregate counts =============
  const ticketCount = isReservation ? batchSize : walkSeats.length;
  const totalErw = isReservation ? erwCount : walkErwCount;
  const totalKind = isReservation ? kindCount : walkKindCount;
  const totalSav = isReservation ? savCount : walkSavCount;

  // ============= Total with voucher discount =============
  const total = useMemo(() => {
    const prices = [];
    for (let i = 0; i < totalErw; i++) prices.push(15);
    for (let i = 0; i < totalSav; i++) prices.push(12);
    for (let i = 0; i < totalKind; i++) prices.push(7);
    prices.sort((a, b) => b - a);
    const remaining = prices.slice(Math.min(voucher, prices.length));
    return Math.round(remaining.reduce((s, p) => s + p, 0) * 100) / 100;
  }, [totalErw, totalKind, totalSav, voucher]);

  const voucherDiscount = useMemo(() => {
    const prices = [];
    for (let i = 0; i < totalErw; i++) prices.push(15);
    for (let i = 0; i < totalSav; i++) prices.push(12);
    for (let i = 0; i < totalKind; i++) prices.push(7);
    prices.sort((a, b) => b - a);
    const waived = prices.slice(0, Math.min(voucher, prices.length));
    return Math.round(waived.reduce((s, p) => s + p, 0) * 100) / 100;
  }, [totalErw, totalKind, totalSav, voucher]);

  const givenNum = parseFloat((given || "0").replace(",", ".")) || 0;
  const change = givenNum - total;

  // ============= Reservation: button handlers =============
  const tapKind = () => {
    if (kindCount + savCount >= batchSize) { showErr('Schon alle Karten zugeordnet. Tipp "Erwachsen", um eine zurückzunehmen.'); return; }
    // pick an unused seat (first that's not in conversions)
    const used = new Set(conversions.map((c) => c.seatKey));
    const free = batchSeats.find((s) => !used.has(s));
    if (!free) return;
    setConversions((c) => [...c, { seatKey: free, cat: "kind" }]);
  };
  const tapSav = () => {
    if (kindCount + savCount >= batchSize) { showErr('Schon alle Karten zugeordnet. Tipp "Erwachsen", um eine zurückzunehmen.'); return; }
    const used = new Set(conversions.map((c) => c.seatKey));
    const free = batchSeats.find((s) => !used.has(s));
    if (!free) return;
    setConversions((c) => [...c, { seatKey: free, cat: "sav" }]);
  };
  const tapErw = () => {
    if (conversions.length === 0) { showErr('Es ist schon alles auf Erwachsen.'); return; }
    setConversions((c) => c.slice(0, -1));
  };

  // ============= Walk-in: button handlers =============
  const walkSetDefault = (cat) => setWalkDefault(cat);
  const walkUndo = () => {
    if (walkOrder.length === 0) return;
    const last = walkOrder[walkOrder.length - 1];
    setWalkCart((c) => { const n = { ...c }; delete n[last]; return n; });
    setWalkOrder((o) => o.slice(0, -1));
  };

  // ============= Seat click on main saalplan =============
  const selectedSeatSet = useMemo(
    () => isReservation ? new Set(batchSeats) : new Set(walkSeats),
    [isReservation, batchSeats, walkSeats]
  );

  const handleSeatClick = (info) => {
    setErr("");
    if (isReservation) {
      // Click in reservation mode: noop (seats are pre-selected via batch)
      return;
    }
    // Walk-in mode
    const key = info.key;
    if (walkCart[key]) {
      setWalkCart((c) => { const n = { ...c }; delete n[key]; return n; });
      setWalkOrder((o) => o.filter((k) => k !== key));
      return;
    }
    if (info.status === "paid" || info.status === "reserved") {
      showErr("Dieser Platz ist nicht frei. Bitte einen grauen Sitz wählen.");
      return;
    }
    setWalkCart((c) => ({ ...c, [key]: walkDefault }));
    setWalkOrder((o) => [...o, key]);
  };

  // ============= Voucher =============
  const tapVoucher = () => {
    if (voucher >= ticketCount) { showErr("Mehr Gutscheine als Karten geht nicht."); return; }
    setVoucher((v) => v + 1);
  };
  const decVoucher = () => setVoucher((v) => Math.max(0, v - 1));

  // ============= Storno (reservation mode only) =============
  const stornoSeats = async (seatList) => {
    if (!seatList || seatList.length === 0) {
      setStornoOpen(false); return;
    }
    try {
      const allOpen = selectedRes.open_seats || [];
      const isAll = seatList.length === allOpen.length && seatList.every((s) => allOpen.includes(s));
      if (isAll) {
        await api.post(`/reservations/${selectedRes.id}/cancel-remaining`);
      } else {
        await api.post(`/reservations/${selectedRes.id}/cancel-seats`, { seats: seatList });
      }
      // If any storno'd seat was part of the active batch, the cashier needs
      // to re-confirm the (smaller) batch — kick back to the seat picker so
      // category counts (Erw/Kind/SAV) reset cleanly.
      const batchTouched = seatList.some((s) => batchSeats.includes(s));
      setStornoOpen(false);
      if (batchTouched) {
        onSuccess({ stornoOnly: true });
      } else {
        // Storno only freed non-batch seats — just refresh the reservation
        // and stay in the cashier view.
        onSuccess({ stornoRefresh: true });
      }
    } catch (e) {
      showErr(friendlyError(e.response?.data?.detail) || e.message);
    }
  };

  // ============= Numpad =============
  const onNumpad = (val) => {
    if (val === "C") { setGiven(""); return; }
    if (val === "←") { setGiven((g) => g.slice(0, -1)); return; }
    setGiven((g) => {
      const next = g + val;
      if (/^\d*(?:[.,]\d{0,2})?$/.test(next)) return next;
      return g;
    });
  };

  // ============= Submit =============
  const buildPayload = () => {
    if (isReservation) {
      const used = new Set(conversions.map((c) => c.seatKey));
      const items = {
        kinder_res: kindCount,
        erwachsene_res: erwCount,
        sav_res: savCount,
        kinder_no_res: 0,
        erwachsene_no_res: 0,
        sav_no_res: 0,
        gutschein: voucher,
      };
      return {
        show_id: showId,
        reservation_id: selectedRes.id,
        items,
        seats: [...batchSeats].sort(sortSeatKey),
      };
    }
    // walk-in
    const items = {
      kinder_res: 0, erwachsene_res: 0, sav_res: 0,
      kinder_no_res: walkKindCount,
      erwachsene_no_res: walkErwCount,
      sav_no_res: walkSavCount,
      gutschein: voucher,
    };
    return {
      show_id: showId,
      reservation_id: null,
      items,
      seats: [...walkSeats].sort(sortSeatKey),
    };
  };

  const requestCash = () => {
    setErr("");
    if (ticketCount === 0) { showErr("Bitte erst Karten hinzufügen."); return; }
    if (givenNum === 0) {
      // Kein Gegeben-Betrag eingegeben → wie "Passend" behandeln (= exakter Betrag)
      setGiven(total.toFixed(2));
      // setGiven ist async → Dialog im nächsten Tick öffnen, sonst sieht er noch givenNum=0
      setTimeout(() => setShowCashConfirm(true), 0);
      return;
    }
    if (givenNum < total) { showErr('Der gegebene Betrag ist kleiner als zu zahlen. Bitte mehr Geld eingeben oder "Passend" drücken.'); return; }
    setShowCashConfirm(true);
  };

  const submitCash = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/transactions", { ...buildPayload(), payment_type: "cash", given: givenNum });
      onSuccess({ tx: data });
      setShowCashConfirm(false);
    } catch (e) {
      showErr(friendlyError(e.response?.data?.detail) || e.message);
    }
    setSubmitting(false);
  };

  const submitInvoice = async ({ club_name, address, email, payment_method, given: invGiven }) => {
    if (ticketCount === 0) throw new Error("Bitte erst Karten hinzufügen.");
    try {
      const { data } = await api.post("/transactions", {
        ...buildPayload(),
        payment_type: "invoice",
        invoice_club_name: club_name,
        invoice_address: address,
        invoice_email: email,
        invoice_payment_method: payment_method || "bank_transfer",
        given: invGiven,
      });
      setShowInvoice(false);
      onSuccess({ tx: data });
    } catch (e) {
      throw new Error(friendlyError(e.response?.data?.detail) || e.message);
    }
  };

  const cashSummary = useMemo(() => ({
    ticketCount, total, given: givenNum, change, voucher, voucherDiscount,
    customerName: isReservation ? selectedRes?.name : "Abendkasse-Verkauf",
    byCategory: [
      { key: "erw", label: "Erwachsen", qty: totalErw, lineTotal: totalErw * 15 },
      { key: "kind", label: "Kind", qty: totalKind, lineTotal: totalKind * 7 },
      { key: "sav", label: "SAV", qty: totalSav, lineTotal: totalSav * 12 },
    ].filter((c) => c.qty > 0),
  }), [ticketCount, total, givenNum, change, voucher, voucherDiscount, isReservation, selectedRes, totalErw, totalKind, totalSav]);

  // ============= UI =============
  // Storno can now act on ANY open seat of the reservation — including ones
  // currently in the active batch. If a batch seat is cancelled we'll reset
  // the batch (see stornoSeats handler).
  const stornoSelectable = isReservation
    ? (selectedRes?.open_seats || [])
    : [];

  // Big category button
  const CatButton = ({ count, label, Icon, onClick, color, testid, disabled }) => (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className={`h-32 rounded-2xl border-2 flex flex-col items-center justify-center gap-1 transition disabled:opacity-40 disabled:cursor-not-allowed ${color}`}>
      <Icon className="h-8 w-8" />
      <span className="text-2xl font-black tracking-tight">{label}</span>
      <span className="font-mono text-4xl font-black tabular-nums">{count}</span>
    </button>
  );

  return (
    <main className="flex-1 grid grid-cols-1 xl:grid-cols-12 gap-3 p-3 overflow-hidden">
      {/* Saalplan left */}
      <section className="xl:col-span-3 hidden xl:flex flex-col overflow-hidden">
        <SeatPlan
          showId={showId}
          selectedRes={selectedRes}
          refreshKey={seatRefresh}
          selectedSeats={selectedSeatSet}
          onSeatClick={handleSeatClick}
        />
        <p className="text-sm text-zinc-600 mt-2 px-1 font-semibold">
          {isReservation
            ? `Aktueller Verkauf: ${batchSize} Sitz${batchSize === 1 ? "" : "e"} (lila umrandet)`
            : "Tippe auf einen grauen Sitz, um eine Karte zu verkaufen."}
        </p>
      </section>

      {/* Center: buttons */}
      <section className="xl:col-span-6 flex flex-col gap-3 overflow-y-auto">
        <div className="bg-white border-2 border-zinc-200 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-zinc-500">
                {isReservation ? "Reservierung" : "Abendkasse"}
              </p>
              <h2 className="text-2xl font-black">
                {isReservation ? selectedRes?.name : "Verkauf ohne Reservierung"}
              </h2>
              {isReservation && (
                <p className="text-sm text-zinc-500 mt-1">
                  {batchSize} Karte{batchSize === 1 ? "" : "n"} in diesem Verkauf · {(selectedRes?.open_seats || []).length} insgesamt noch offen
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1.5 items-end">
              {isReservation && (
                <button onClick={onBackToSeats} data-testid="cashier-back-seats"
                  className="h-12 px-3 rounded-lg border-2 border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-900 font-bold text-sm flex items-center gap-1.5">
                  <ChevronLeft className="h-4 w-4" /> Karten ändern
                </button>
              )}
              <button onClick={onBack} data-testid="cashier-back"
                className="h-12 px-3 rounded-lg border-2 border-zinc-200 hover:bg-zinc-50 font-bold text-sm flex items-center gap-1.5">
                <ChevronLeft className="h-4 w-4" /> Start
              </button>
            </div>
          </div>

          {/* The 5 big buttons */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
            {isReservation ? (
              <>
                <CatButton testid="kassa-btn-erw" count={erwCount} label="Erwachsen"
                  Icon={UserIcon} onClick={tapErw}
                  color="bg-white border-zinc-300 hover:bg-zinc-50 text-zinc-900" />
                <CatButton testid="kassa-btn-kind" count={kindCount} label="Kind"
                  Icon={Baby} onClick={tapKind}
                  color="bg-sky-50 border-sky-300 hover:bg-sky-100 text-sky-900" />
                <CatButton testid="kassa-btn-sav" count={savCount} label="SAV"
                  Icon={BadgeCheck} onClick={tapSav}
                  color="bg-amber-50 border-amber-300 hover:bg-amber-100 text-amber-900" />
                <CatButton testid="kassa-btn-voucher" count={voucher} label="Gutschein"
                  Icon={Ticket} onClick={tapVoucher}
                  color="bg-purple-50 border-purple-300 hover:bg-purple-100 text-purple-900" />
                <button onClick={() => setStornoOpen(true)} data-testid="kassa-btn-storno"
                  disabled={stornoSelectable.length === 0}
                  className="h-32 rounded-2xl border-2 border-red-300 bg-red-50 hover:bg-red-100 disabled:opacity-40 text-red-900 flex flex-col items-center justify-center gap-1">
                  <Trash2 className="h-8 w-8" />
                  <span className="text-2xl font-black tracking-tight">Storno</span>
                  <span className="text-xs font-bold">Sitz freigeben</span>
                </button>
                <button onClick={tapErw} data-testid="kassa-btn-undo"
                  disabled={conversions.length === 0}
                  className="h-32 rounded-2xl border-2 border-zinc-200 bg-white hover:bg-amber-50 hover:border-amber-300 disabled:opacity-40 text-zinc-700 flex flex-col items-center justify-center gap-1">
                  <Undo2 className="h-8 w-8" />
                  <span className="text-2xl font-black tracking-tight">Rückgängig</span>
                  <span className="text-xs font-bold">Letzten Klick zurück</span>
                </button>
              </>
            ) : (
              <>
                <CatButton testid="kassa-walk-erw" count={walkErwCount} label="Erwachsen"
                  Icon={UserIcon} onClick={() => walkSetDefault("erwachsene_no_res")}
                  color={`${walkDefault === "erwachsene_no_res" ? "bg-zinc-950 border-zinc-950 text-white" : "bg-white border-zinc-300 hover:bg-zinc-50 text-zinc-900"}`} />
                <CatButton testid="kassa-walk-kind" count={walkKindCount} label="Kind"
                  Icon={Baby} onClick={() => walkSetDefault("kinder_no_res")}
                  color={`${walkDefault === "kinder_no_res" ? "bg-sky-600 border-sky-700 text-white" : "bg-sky-50 border-sky-300 hover:bg-sky-100 text-sky-900"}`} />
                <CatButton testid="kassa-walk-sav" count={walkSavCount} label="SAV"
                  Icon={BadgeCheck} onClick={() => walkSetDefault("sav_no_res")}
                  color={`${walkDefault === "sav_no_res" ? "bg-amber-600 border-amber-700 text-white" : "bg-amber-50 border-amber-300 hover:bg-amber-100 text-amber-900"}`} />
                <CatButton testid="kassa-walk-voucher" count={voucher} label="Gutschein"
                  Icon={Ticket} onClick={tapVoucher}
                  color="bg-purple-50 border-purple-300 hover:bg-purple-100 text-purple-900" />
                <button onClick={walkUndo} data-testid="kassa-walk-undo"
                  disabled={walkOrder.length === 0}
                  className="h-32 rounded-2xl border-2 border-zinc-200 bg-white hover:bg-amber-50 hover:border-amber-300 disabled:opacity-40 text-zinc-700 flex flex-col items-center justify-center gap-1">
                  <Undo2 className="h-8 w-8" />
                  <span className="text-2xl font-black tracking-tight">Rückgängig</span>
                  <span className="text-xs font-bold">Letzten Sitz zurück</span>
                </button>
                <button onClick={decVoucher} data-testid="kassa-walk-voucher-minus"
                  disabled={voucher === 0}
                  className="h-32 rounded-2xl border-2 border-purple-200 bg-white hover:bg-purple-50 disabled:opacity-40 text-purple-800 flex flex-col items-center justify-center gap-1">
                  <Minus className="h-8 w-8" />
                  <span className="text-2xl font-black tracking-tight">Gutschein</span>
                  <span className="text-xs font-bold">weniger</span>
                </button>
              </>
            )}
          </div>

          {voucher > 0 && (
            <div className="bg-purple-50 border-2 border-purple-200 rounded-xl px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-base font-bold text-purple-900">{voucher}× Gutschein eingelöst</p>
                <p className="text-xs text-purple-700">erlässt jeweils die teuerste Karte</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={decVoucher} data-testid="voucher-minus"
                  className="h-10 w-10 rounded-lg border-2 border-purple-300 bg-white hover:bg-purple-100 flex items-center justify-center">
                  <Minus className="h-4 w-4" />
                </button>
                <span data-testid="voucher-qty" className="font-mono text-xl font-black w-8 text-center">{voucher}</span>
              </div>
              <span data-testid="voucher-discount" className="font-mono text-base font-black text-purple-800">– {formatEuro(voucherDiscount)}</span>
            </div>
          )}
        </div>

        {/* Total */}
        <div className="bg-zinc-950 text-white rounded-2xl p-5 flex items-end justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-zinc-400">Zu zahlen</p>
            <p className="text-sm text-zinc-300 font-bold">{ticketCount} Karte{ticketCount !== 1 ? "n" : ""}</p>
          </div>
          <p data-testid="kassa-total" className="font-mono text-6xl md:text-7xl font-black tracking-tighter tabular-nums">{formatEuro(total)}</p>
        </div>
      </section>

      {/* Right: Numpad + Pay buttons */}
      <section className="xl:col-span-3 flex flex-col gap-3 overflow-y-auto">
        <div className="bg-white border-2 border-zinc-200 rounded-2xl p-3">
          <div className="flex items-end justify-between mb-3">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-zinc-500">Gegeben</p>
              <p data-testid="kassa-given" className="font-mono text-3xl font-black tabular-nums">
                {given ? formatEuro(givenNum) : "—"}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-zinc-500">Rückgeld</p>
              <p data-testid="kassa-change" className={`font-mono text-3xl font-black tabular-nums ${change < 0 ? "text-red-600" : "text-emerald-700"}`}>
                {given ? formatEuro(change) : "—"}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-2 mb-2">
            <button data-testid="kassa-quick-passend" onClick={() => setGiven(total.toFixed(2))}
              className="h-14 rounded-lg border-2 border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-900 font-black text-base">Passend</button>
            <button data-testid="kassa-quick-20" onClick={() => setGiven("20")}
              className="h-14 rounded-lg border-2 border-zinc-200 bg-white hover:bg-zinc-100 font-black text-base">20</button>
            <button data-testid="kassa-quick-50" onClick={() => setGiven("50")}
              className="h-14 rounded-lg border-2 border-zinc-200 bg-white hover:bg-zinc-100 font-black text-base">50</button>
            <button data-testid="kassa-quick-100" onClick={() => setGiven("100")}
              className="h-14 rounded-lg border-2 border-zinc-200 bg-white hover:bg-zinc-100 font-black text-base">100</button>
          </div>

          <div className="grid grid-cols-3 gap-2 mb-2">
            {["1","2","3","4","5","6","7","8","9"].map((d) => (
              <button key={d} data-testid={`numpad-${d}`} onClick={() => onNumpad(d)}
                className="h-14 rounded-lg border-2 border-zinc-200 bg-white hover:bg-zinc-100 active:bg-zinc-200 font-mono text-2xl font-black">{d}</button>
            ))}
            <button data-testid="numpad-comma" onClick={() => onNumpad(",")} className="h-14 rounded-lg border-2 border-zinc-200 bg-white hover:bg-zinc-100 font-mono text-2xl font-black">,</button>
            <button data-testid="numpad-0" onClick={() => onNumpad("0")} className="h-14 rounded-lg border-2 border-zinc-200 bg-white hover:bg-zinc-100 font-mono text-2xl font-black">0</button>
            <button data-testid="numpad-back" onClick={() => onNumpad("←")} className="h-14 rounded-lg border-2 border-zinc-200 bg-white hover:bg-zinc-100 font-mono text-2xl font-black">←</button>
          </div>

          {err && (
            <div data-testid="kassa-error" role="alert"
              className="mb-2 text-base font-bold text-red-800 bg-red-50 border-2 border-red-300 rounded-xl px-3 py-3 flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
              <span>{err}</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button data-testid="kassa-pay-invoice" onClick={() => setShowInvoice(true)}
            disabled={submitting || ticketCount === 0}
            className="h-20 rounded-2xl bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-lg font-black flex items-center justify-center gap-2">
            <Receipt className="h-6 w-6" /> Rechnung
          </button>
          <button data-testid="kassa-pay-cash" onClick={requestCash}
            disabled={submitting || ticketCount === 0}
            className="h-20 rounded-2xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-lg font-black flex items-center justify-center gap-2">
            <Banknote className="h-6 w-6" /> Bar
          </button>
        </div>
      </section>

      <InvoiceDialog open={showInvoice} total={total} onClose={() => setShowInvoice(false)} onConfirm={submitInvoice} />
      <CashConfirmDialog
        open={showCashConfirm}
        summary={cashSummary}
        onConfirm={submitCash}
        onCancel={() => setShowCashConfirm(false)}
        submitting={submitting}
      />

      {/* Storno modal */}
      {isReservation && (
        <SeatPickerModal
          open={stornoOpen}
          title={`Sitze stornieren · ${selectedRes?.name}`}
          subtitle='Tippe alle Sitze an, die freigegeben werden sollen. Mit „Stornieren" bestätigen.'
          seats={stornoSelectable}
          inBatch={[]}
          initialSelected={[]}
          confirmLabel="Stornieren"
          confirmStyle="danger"
          perSeatConfirm={false}
          emptyHint="Keine offenen Sitze zum Stornieren."
          onConfirm={(picked) => stornoSeats(picked)}
          onClose={() => setStornoOpen(false)}
        />
      )}
    </main>
  );
}

// ============================================================
// Invoice Dialog (inline)
// ============================================================
function InvoiceDialog({ open, total, onClose, onConfirm }) {
  const [clubName, setClubName] = useState("");
  const [address, setAddress] = useState("");
  const [email, setEmail] = useState("");
  const [given, setGiven] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  // Rechnungen werden immer bar bezahlt (Theatergruppe Seekirchen Standard).
  const method = "cash";
  useEffect(() => {
    if (open) { setClubName(""); setAddress(""); setEmail(""); setGiven(""); setErr(""); }
  }, [open]);
  if (!open) return null;
  const givenNum = parseFloat((given || "0").replace(",", ".")) || 0;
  const change = givenNum - total;
  const submit = async (e) => {
    e.preventDefault();
    if (givenNum < total) { setErr("Gegebener Betrag ist kleiner als der Rechnungsbetrag."); return; }
    setSaving(true); setErr("");
    try {
      await onConfirm({ club_name: clubName, address, email, payment_method: "cash", given: givenNum });
    } catch (e) {
      setErr(e.message || "Fehler beim Speichern");
    }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit} className="p-8 space-y-5">
          <h2 className="text-3xl font-black tracking-tight">Rechnung (Bar)</h2>
          <p className="text-zinc-500 text-lg -mt-3">Betrag: <span className="font-mono font-bold text-zinc-900">{formatEuro(total)}</span> · Verein zahlt sofort bar.</p>
          <div>
            <label className="text-sm uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2 block">Vereinsname *</label>
            <input data-testid="invoice-club-name" value={clubName} onChange={(e) => setClubName(e.target.value)} required autoFocus
              className="w-full h-14 px-4 text-lg border-2 border-zinc-300 rounded-xl focus:outline-none focus:border-zinc-950" />
          </div>
          <div>
            <label className="text-sm uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2 block">Adresse</label>
            <textarea data-testid="invoice-address" value={address} onChange={(e) => setAddress(e.target.value)} rows={3}
              className="w-full px-4 py-3 text-base border-2 border-zinc-300 rounded-xl focus:outline-none focus:border-zinc-950" />
          </div>
          <div>
            <label className="text-sm uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2 block">E-Mail</label>
            <input data-testid="invoice-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full h-14 px-4 text-lg border-2 border-zinc-300 rounded-xl focus:outline-none focus:border-zinc-950" />
          </div>
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4 space-y-3">
            <label className="text-sm uppercase tracking-[0.2em] font-bold text-emerald-900 mb-2 block">Gegeben (€)</label>
            <input data-testid="invoice-cash-given" type="number" min="0" step="0.01" value={given} onChange={(e) => setGiven(e.target.value)}
              placeholder={total.toFixed(2)}
              className="w-full h-14 px-4 text-lg border-2 border-emerald-300 rounded-xl focus:outline-none focus:border-emerald-600 bg-white" />
            {given && <p data-testid="invoice-cash-change" className="text-base font-mono">Rückgeld: <strong className={change < 0 ? "text-red-700" : "text-emerald-800"}>{formatEuro(change)}</strong></p>}
          </div>
          {err && <div className="text-base text-red-700 bg-red-50 border-2 border-red-200 rounded-xl px-4 py-3">{err}</div>}
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={onClose} className="h-14 px-6 rounded-xl border-2 border-zinc-200 hover:bg-zinc-50 font-bold text-base">Abbrechen</button>
            <button data-testid="invoice-confirm" type="submit" disabled={saving}
              className="h-14 px-8 rounded-xl text-white font-black text-base disabled:opacity-60 bg-emerald-600 hover:bg-emerald-700">
              {saving ? "Speichern…" : "Bar kassieren + Rechnung"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// Success Dialog (inline)
// ============================================================
function SuccessDialog({ open, tx, onClose, onPrintInvoice }) {
  const [emailTo, setEmailTo] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [emailErr, setEmailErr] = useState("");
  useEffect(() => {
    if (open && tx) { setEmailTo(tx.invoice_email || ""); setEmailSent(false); setEmailErr(""); }
  }, [open, tx]);
  if (!open || !tx) return null;
  const sendEmail = async () => {
    if (!emailTo) { setEmailErr("Bitte E-Mail-Adresse eingeben"); return; }
    setSending(true); setEmailErr("");
    try {
      await api.post(`/transactions/${tx.id}/invoice/email`, { to: emailTo });
      setEmailSent(true);
    } catch (e) {
      setEmailErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setSending(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-10 text-center">
        <div className="h-20 w-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-5">
          <Check className="h-12 w-12 text-emerald-700" />
        </div>
        <h2 className="text-4xl font-black tracking-tight mb-2">Erfolgreich!</h2>
        <p className="text-zinc-500 text-lg mb-6">
          {tx.payment_type === "cash" ? "Barzahlung gespeichert." : `Rechnung ${tx.invoice_number} erstellt.`}
        </p>
        <div className="bg-zinc-50 border-2 border-zinc-200 rounded-xl p-6 mb-6">
          {tx.payment_type === "cash" ? (
            <>
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-zinc-500 mb-1">Rückgeld</p>
              <p className="font-mono text-6xl font-black text-emerald-700 tabular-nums">{formatEuro(tx.change)}</p>
              <p className="text-base text-zinc-500 mt-3">Bar bezahlt {formatEuro(tx.total)}</p>
            </>
          ) : tx.invoice_payment_method === "cash" ? (
            <>
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-zinc-500 mb-1">Rückgeld</p>
              <p className="font-mono text-5xl font-black text-emerald-700 tabular-nums">{formatEuro(tx.change || 0)}</p>
              <p className="text-base text-zinc-500 mt-3">Bar bezahlt {formatEuro(tx.total)} · Rechnung {tx.invoice_number}</p>
            </>
          ) : (
            <>
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-zinc-500 mb-1">Rechnungsbetrag</p>
              <p className="font-mono text-5xl font-black tabular-nums">{formatEuro(tx.total)}</p>
              <p className="text-base text-zinc-500 mt-3">{tx.invoice_club_name}</p>
            </>
          )}
        </div>
        {tx.payment_type === "invoice" && (
          <div className="mb-4 border-2 border-zinc-200 rounded-xl p-4 text-left">
            <p className="text-sm uppercase tracking-[0.25em] font-bold text-zinc-500 mb-2">PDF per E-Mail</p>
            <div className="flex gap-2">
              <input data-testid="email-to-input" type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
                placeholder="empfaenger@verein.at" disabled={emailSent}
                className="flex-1 h-12 px-3 text-base border-2 border-zinc-300 rounded-lg focus:outline-none focus:border-zinc-950 disabled:bg-zinc-50" />
              {emailSent ? (
                <button data-testid="email-sent" disabled className="h-12 px-4 rounded-lg bg-emerald-100 text-emerald-800 font-bold text-base flex items-center gap-2">
                  <Check className="h-5 w-5" /> Gesendet
                </button>
              ) : (
                <button data-testid="email-send-btn" onClick={sendEmail} disabled={sending || !emailTo}
                  className="h-12 px-4 rounded-lg bg-zinc-950 hover:bg-zinc-800 text-white text-base font-bold flex items-center gap-2 disabled:opacity-50">
                  {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mail className="h-5 w-5" />}
                  Senden
                </button>
              )}
            </div>
            {emailErr && <p className="text-sm text-red-700 mt-2">{emailErr}</p>}
          </div>
        )}
        <div className="flex gap-3">
          {tx.payment_type === "invoice" && (
            <button data-testid="print-invoice-btn" onClick={onPrintInvoice}
              className="flex-1 h-16 rounded-xl border-2 border-zinc-200 hover:bg-zinc-50 font-bold text-base flex items-center justify-center gap-2">
              <FileText className="h-5 w-5" /> Rechnung-PDF
            </button>
          )}
          <button data-testid="success-continue-btn" onClick={onClose}
            className="flex-1 h-16 rounded-xl bg-zinc-950 hover:bg-zinc-800 text-white font-black text-lg">Weiter</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Retoure (Refund) screen — click a paid seat to return the ticket
// ============================================================
function RefundScreen({ showId, seatRefresh, setSeatRefresh, onBack, onDone, showErr }) {
  const [pickedSeats, setPickedSeats] = useState([]); // array of seat-info objects
  const [submitting, setSubmitting] = useState(false);
  const [lastRefund, setLastRefund] = useState(null); // { count, total }
  const [localErr, setLocalErr] = useState("");

  const pickedKeys = new Set(pickedSeats.map((p) => p.key));

  const handleSeatClick = (info) => {
    setLocalErr("");
    setLastRefund(null);
    if (info.status !== "paid") return;
    setPickedSeats((cur) => {
      // Toggle: if already picked, remove
      if (cur.some((s) => s.key === info.key)) {
        return cur.filter((s) => s.key !== info.key);
      }
      return [...cur, info];
    });
  };

  const doRefund = async () => {
    if (pickedSeats.length === 0) return;
    setSubmitting(true);
    setLocalErr("");
    let totalAmount = 0;
    let okCount = 0;
    let firstErr = "";
    for (const seat of pickedSeats) {
      try {
        const { data } = await api.post("/seats/refund", {
          show_id: showId,
          seat: seat.key,
        });
        totalAmount += Number(data.refund_amount || 0);
        okCount += 1;
      } catch (e) {
        if (!firstErr) firstErr = `${seat.key}: ${formatApiErrorDetail(e.response?.data?.detail) || e.message}`;
      }
    }
    setSeatRefresh((n) => n + 1);
    onDone?.();
    setSubmitting(false);
    if (okCount > 0) {
      setLastRefund({ count: okCount, total: totalAmount });
    }
    setPickedSeats([]);
    if (firstErr) {
      setLocalErr(firstErr);
      showErr?.(firstErr);
    }
  };

  return (
    <main className="flex-1 grid grid-cols-1 xl:grid-cols-12 gap-3 p-3 overflow-hidden">
      <section className="xl:col-span-6 flex flex-col overflow-hidden">
        <SeatPlan
          showId={showId}
          selectedRes={null}
          refreshKey={seatRefresh}
          selectedSeats={pickedKeys}
          onSeatClick={handleSeatClick}
          paidClickable={true}
        />
        <p className="text-sm text-zinc-600 mt-2 px-1 font-semibold">
          Tippe auf alle <span className="text-emerald-700 font-black">grünen (bezahlten) Sitze</span>, die retourniert werden sollen. Erneutes Tippen entfernt einen Sitz aus der Auswahl.
        </p>
      </section>

      <section className="xl:col-span-6 flex flex-col gap-3">
        <div className="bg-white border-2 border-zinc-200 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-zinc-500">Modus</p>
              <h2 className="text-2xl font-black">Karten-Retoure</h2>
              <p className="text-sm text-zinc-500 mt-1">Sitze im Plan auswählen, dann gesammelt retournieren. Der Betrag wird automatisch ermittelt.</p>
            </div>
            <button onClick={onBack} data-testid="refund-back"
              className="h-12 px-3 rounded-lg border-2 border-zinc-200 hover:bg-zinc-50 font-bold text-sm flex items-center gap-1.5">
              <ChevronLeft className="h-4 w-4" /> Zurück
            </button>
          </div>

          {lastRefund && pickedSeats.length === 0 && (
            <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-4 mb-4" data-testid="refund-success">
              <p className="text-base font-black text-emerald-900">
                <Check className="h-5 w-5 inline mr-1" /> {lastRefund.count} Karte{lastRefund.count === 1 ? "" : "n"} retourniert
              </p>
              <p className="text-sm text-emerald-800 mt-1">
                Rückerstattung gesamt: <span className="font-mono font-black">{formatEuro(lastRefund.total)}</span> — Sitze sind wieder frei.
              </p>
            </div>
          )}

          {pickedSeats.length > 0 ? (
            <div className="space-y-4">
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
                <p className="text-sm uppercase tracking-[0.25em] font-bold text-red-800">Ausgewählte Sitze ({pickedSeats.length})</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {pickedSeats.map((s) => (
                    <span key={s.key} data-testid={`refund-picked-${s.key}`}
                      className="font-mono text-base font-black bg-red-100 border-2 border-red-300 text-red-900 px-2 py-1 rounded-lg">
                      {s.key}
                    </span>
                  ))}
                </div>
              </div>
              <button onClick={doRefund} disabled={submitting} data-testid="refund-confirm"
                className="w-full h-16 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-black text-lg">
                {submitting ? "Werden retourniert…" : `${pickedSeats.length} Karte${pickedSeats.length === 1 ? "" : "n"} retournieren`}
              </button>
              {localErr && (
                <div data-testid="refund-error" className="bg-red-50 border-2 border-red-300 rounded-xl p-4 text-red-800 font-bold text-base">
                  {localErr}
                </div>
              )}
              <button onClick={() => { setPickedSeats([]); setLocalErr(""); }} data-testid="refund-cancel"
                className="w-full h-14 rounded-xl border-2 border-zinc-200 hover:bg-zinc-50 font-bold text-base">
                Auswahl leeren
              </button>
            </div>
          ) : (
            <p className="text-zinc-500 text-base text-center py-6">Noch kein Sitz gewählt — bitte im Saalplan einen oder mehrere bezahlte Sitze antippen.</p>
          )}
        </div>
      </section>
    </main>
  );
}

// ============================================================
// Main KassaPage
// ============================================================
export default function KassaPage() {
  const { showId } = useParams();
  const { config } = useAuth();
  const navigate = useNavigate();

  const [show, setShow] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [step, setStep] = useState("mode"); // "mode" | "pick_res" | "pick_seats" | "cashier"
  const [selectedRes, setSelectedRes] = useState(null);
  const [batchSeats, setBatchSeats] = useState([]); // array of "row-seat"
  const [mode, setMode] = useState(null); // "reservation" | "walkin"
  const [success, setSuccess] = useState(null);
  const [err, setErr] = useState("");
  const [seatRefresh, setSeatRefresh] = useState(0);
  const [helpOpen, setHelpOpen] = useFirstVisitTour();
  const [startCashOpen, setStartCashOpen] = useState(false);
  const [startCashEditMode, setStartCashEditMode] = useState(false);
  const errTimerRef = useRef(null);

  const load = useCallback(async () => {
    const [s, r] = await Promise.all([
      api.get(`/shows/${showId}`),
      api.get(`/shows/${showId}/reservations`),
    ]);
    setShow(s.data);
    setReservations(r.data);
    setSelectedRes((cur) => (cur ? r.data.find((x) => x.id === cur.id) || null : null));
  }, [showId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (show && !show.start_cash_confirmed_at) {
      setStartCashEditMode(false);
      setStartCashOpen(true);
    }
  }, [show]);

  const showErr = useCallback((msg) => {
    setErr(msg);
    if (errTimerRef.current) clearTimeout(errTimerRef.current);
    errTimerRef.current = setTimeout(() => setErr(""), 5000);
  }, []);

  const confirmStartCash = async ({ amount }) => {
    try {
      const { data } = await api.post(`/shows/${showId}/start-cash`, { amount });
      setShow(data);
      setStartCashOpen(false);
    } catch (e) {
      throw new Error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const goHome = () => {
    setStep("mode"); setMode(null); setSelectedRes(null); setBatchSeats([]); setErr("");
  };

  const pickReservationMode = () => { setMode("reservation"); setStep("pick_res"); };
  const pickWalkInMode = () => { setMode("walkin"); setBatchSeats([]); setStep("cashier"); };
  const pickRefundMode = () => { setMode("refund"); setStep("refund"); };
  const pickReservation = (r) => {
    setSelectedRes(r);
    setBatchSeats([]); // will be set after the seat picker
    setStep("pick_seats");
  };
  const confirmSeatBatch = (seats) => {
    setBatchSeats(seats);
    setStep("cashier");
  };

  const handleCashierResult = async (opts) => {
    // opts.tx: transaction succeeded → show success then return to step="mode"
    // opts.stornoOnly: a batch seat was cancelled → reset batch and go to picker
    // opts.stornoRefresh: a non-batch seat was cancelled → just refresh, stay
    const r = await api.get(`/shows/${showId}/reservations`);
    const s = await api.get(`/shows/${showId}`);
    setShow(s.data);
    setReservations(r.data);
    const refreshedRes = selectedRes ? r.data.find((x) => x.id === selectedRes.id) || null : null;
    setSelectedRes(refreshedRes);
    setSeatRefresh((n) => n + 1);
    if (opts?.tx) {
      setSuccess(opts.tx);
      setStep("mode"); setMode(null); setSelectedRes(null); setBatchSeats([]);
    } else if (opts?.stornoOnly) {
      setBatchSeats([]);
      // If nothing left to kassier (e.g. last open seat was storno'd), go home.
      if (!refreshedRes || (refreshedRes.open_seats || []).length === 0) {
        setStep("mode"); setMode(null); setSelectedRes(null);
      } else {
        setStep("pick_seats");
      }
    }
    // stornoRefresh: nothing to change locally; refresh above already done
  };

  const printInvoice = () => {
    if (!success) return;
    window.open(`${API_BASE}/transactions/${success.id}/invoice.pdf`, "_blank");
  };

  if (!config) return <div className="p-10 text-zinc-500">Lade…</div>;

  return (
    <div className="h-screen flex flex-col bg-zinc-50 overflow-hidden">
      <AppHeader
        title={show ? `Kassa · ${show.title}` : "Kassa"}
        right={
          <>
            {show?.start_cash_confirmed_at && (
              <button
                data-testid="startcash-indicator"
                onClick={() => { setStartCashEditMode(true); setStartCashOpen(true); }}
                title="Anfangs-Wechselgeld ändern"
                className="h-12 px-3 rounded-lg border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-900 font-bold text-sm flex items-center gap-2">
                <Wallet className="h-4 w-4" />
                <span className="hidden md:inline">Wechselgeld</span>
                <span className="font-mono font-black tabular-nums">{formatEuro(show.start_cash)}</span>
                <Pencil className="h-3.5 w-3.5 opacity-60" />
              </button>
            )}
            <HelpButton onClick={() => setHelpOpen(true)} />
            <button data-testid="kassa-to-abschluss" onClick={() => navigate(`/shows/${showId}/abschluss`)}
              className="h-12 px-4 rounded-lg border-2 border-zinc-200 hover:bg-zinc-50 font-bold text-sm flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4" /> Tagesabschluss
            </button>
          </>
        }
      />

      {/* Step indicator */}
      <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border-b-2 border-blue-200" data-testid="step-indicator">
        {[
          { n: 1, label: "Modus", active: step === "mode" },
          { n: 2, label: "Reservierung", active: step === "pick_res", show: mode === "reservation" || step === "pick_res" },
          { n: 3, label: "Sitze", active: step === "pick_seats", show: mode === "reservation" },
          { n: 4, label: "Kassieren", active: step === "cashier" },
        ].filter((s) => s.show !== false).map((s, i, arr) => {
          const isActive = s.active;
          const isDone = !isActive && (
            (step === "pick_res" && s.n === 1) ||
            (step === "pick_seats" && s.n <= 2) ||
            (step === "cashier" && s.n <= 3)
          );
          return (
            <div key={s.n} className="flex items-center gap-2">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center font-black text-sm ${
                isDone ? "bg-emerald-600 text-white" :
                isActive ? "bg-blue-600 text-white" : "bg-zinc-200 text-zinc-500"
              }`}>
                {isDone ? <Check className="h-4 w-4" /> : s.n}
              </div>
              <span className={`text-sm font-bold ${isActive ? "text-blue-900" : isDone ? "text-emerald-700" : "text-zinc-500"}`}>
                {s.label}
              </span>
              {i < arr.length - 1 && <span className="text-zinc-300 mx-1">›</span>}
            </div>
          );
        })}
      </div>

      {step === "mode" && (
        <ModeSelectScreen onPickReservation={pickReservationMode} onPickWalkIn={pickWalkInMode} onPickRefund={pickRefundMode} />
      )}

      {step === "refund" && (
        <RefundScreen
          showId={showId}
          seatRefresh={seatRefresh}
          setSeatRefresh={setSeatRefresh}
          onBack={goHome}
          onDone={async () => { await load(); }}
          showErr={showErr}
        />
      )}

      {step === "pick_res" && (
        <ReservationPickerScreen
          reservations={reservations}
          onPick={pickReservation}
          onBack={goHome}
        />
      )}

      {step === "pick_seats" && selectedRes && (
        <SeatPickerModal
          open={true}
          title={`Welche Karten werden jetzt abgegeben?`}
          subtitle={`${selectedRes.name} · ${(selectedRes.open_seats || []).length} offene Sitze`}
          seats={selectedRes.open_seats || []}
          initialSelected={selectedRes.open_seats || []}
          confirmLabel="Weiter mit"
          confirmStyle="primary"
          onConfirm={confirmSeatBatch}
          onClose={() => { setStep("pick_res"); setSelectedRes(null); }}
        />
      )}

      {step === "cashier" && (
        <CashierScreen
          mode={mode}
          show={show}
          showId={showId}
          selectedRes={selectedRes}
          batchSeats={batchSeats}
          walkInSeats={[]}
          reservations={reservations}
          seatRefresh={seatRefresh}
          setSeatRefresh={setSeatRefresh}
          config={config}
          onBack={goHome}
          onBackToSeats={() => { setBatchSeats([]); setStep("pick_seats"); }}
          onSuccess={handleCashierResult}
          navigate={navigate}
          showErr={showErr}
          err={err}
          setErr={setErr}
        />
      )}

      <SuccessDialog open={!!success} tx={success} onClose={() => setSuccess(null)} onPrintInvoice={printInvoice} />
      <HelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
      <StartCashDialog
        open={startCashOpen}
        defaultAmount={show?.start_cash ?? 1150}
        showTitle={show?.title}
        showDate={show?.date}
        dismissible={startCashEditMode}
        onConfirm={confirmStartCash}
        onClose={() => setStartCashOpen(false)}
      />
    </div>
  );
}
